import React, { useState } from "react";
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "carbon-react/lib/components/table";
import Link from "carbon-react/lib/components/link";
import Spinner from "carbon-react/lib/components/loader";
import {
  getAuditOperationDisplayValue,
  getAuditOperationTypeDisplayValue,
} from "../../constants/model";
import SuccessFailureIcon from "../common/SuccessFailureIcon";
import TableHeaderWithSortIndicator, {
  sortOrder,
} from "../common/tableHeaderWithSortIndicator/TableHeaderWithSortIndicator";
import SimpleTablePager from "../common/simpleTablePager/SimpleTablePager";
import AuditLogDialog from "./auditLogDialog/AuditLogDialog";
import { useLocalization } from "../../contexts/localization/Localization";

const AuditLogTable = ({
  auditLogResultSet,
  loading,
  onPagePrev,
  onPageNext,
  pageNo,
  lastPage,
  renderSubjectColumn = true,
}) => {
  const [showLogEntryDialog, setShowLogEntryDialog] = useState(false);
  const [selectedLogEntry, setSelectedLogEntry] = useState(null);
  const { getLocalizedDateStringFromUtcValue } = useLocalization();

  const closeLogEntryDialog = () => {
    setShowLogEntryDialog(false);
    setSelectedLogEntry(null);
  };

  const auditLogs = auditLogResultSet.auditLogs || [];

  const getStatusCellContent = (auditLogEntry) => {
    const tooltip = auditLogEntry.completed
      ? null
      : `Status: ${auditLogEntry.status?.code || "N/A"}. Http Status Code: ${
          auditLogEntry.status?.httpErrorStatusCode || "N/A"
        }`;

    return (
      <SuccessFailureIcon
        className="audit-log-table-icon"
        success={auditLogEntry.completed}
        tooltipMessage={tooltip}
      />
    );
  };

  const onLogEntrySelected = (logEntry) => {
    setSelectedLogEntry(logEntry);
    setShowLogEntryDialog(true);
  };

  const tableRows = auditLogs.map((auditLog, key) => {
    return (
      <TableRow id={`audit-log-table-row-${key}`} key={key}>
        <TableCell id={`audit-log-table-cell-date-${key}`}>
          {getLocalizedDateStringFromUtcValue(auditLog.createdUtc)}
        </TableCell>
        {renderSubjectColumn ? (
          <TableCell id={`audit-log-table-cell-subject-email-${key}`}>
            {auditLog.subjectEmail}
          </TableCell>
        ) : null}
        <TableCell id={`audit-log-table-cell-operation-type-${key}`}>
          {getAuditOperationTypeDisplayValue(auditLog.operationType)}
        </TableCell>
        <TableCell id={`audit-log-table-cell-operation-${key}`}>
          {getAuditOperationDisplayValue(auditLog.operation)}
        </TableCell>
        <TableCell id={`audit-log-table-cell-status-${key}`} align="center">
          {getStatusCellContent(auditLog)}
        </TableCell>
        <TableCell id={`audit-log-table-cell-agent-email-${key}`}>
          {auditLog.agent?.email}
        </TableCell>
        <TableCell
          id={`audit-log-table-cell-agent-view-detail-${key}`}
          align="center"
        >
          <Link onClick={() => onLogEntrySelected(auditLog)}>View detail</Link>
        </TableCell>
      </TableRow>
    );
  });

  const tableHeader = (
    <TableRow id="audit-log-table-header">
      <TableHeaderWithSortIndicator
        id="audit-log-table-header-date"
        sortOrder={sortOrder.desc}
      >
        Date
      </TableHeaderWithSortIndicator>
      {renderSubjectColumn ? (
        <TableHeader id="audit-log-table-header-subject">Subject</TableHeader>
      ) : null}
      <TableHeader id="audit-log-table-header-operation-type">
        Operation type
      </TableHeader>
      <TableHeader id="audit-log-table-header-operation">Operation</TableHeader>
      <TableHeader id="audit-log-table-header-completed" align="center">
        Completed
      </TableHeader>
      <TableHeader id="audit-log-table-header-agent">Agent</TableHeader>
      <TableHeader></TableHeader>
    </TableRow>
  );

  const gridSpinner = (
    <TableRow
      key="__loading__"
      selectable={false}
      highlightable={false}
      hideMultiSelect
    >
      <TableCell colSpan="100" align="center">
        <Spinner size="small" />
      </TableCell>
    </TableRow>
  );

  return (
    <div id="audit-log-table" className="audit-log-table">
      <Table isZebra thead={tableHeader} shrink>
        {loading === true ? gridSpinner : tableRows}
      </Table>
      <SimpleTablePager
        appearBusy={loading}
        pageNo={pageNo}
        lastPage={lastPage}
        onPagePrev={onPagePrev}
        onPageNext={onPageNext}
      />
      <AuditLogDialog
        open={showLogEntryDialog}
        auditLogEntry={selectedLogEntry}
        onClose={() => closeLogEntryDialog()}
      />
    </div>
  );
};

export default AuditLogTable;
